import { Component } from "react";
import { store } from "src/store/store";
import { compose } from "recompose";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import environment, { electronOnly } from "src/utils/environment";
import debug from "src/modules/debug-logger";
import Dialog from "src/components/Dialog";
import {
  isAnonymousUser,
  hasPremium,
  isFreemium
} from "src/modules/auth/duck/selectors";
import { webLinks } from "src/browser/web-links";
import { createTemporarySubscription } from "src/modules/purchase/ajax/cloud";
import { synchronizeCloudAccount } from "src/modules/auth/duck/epics";
import FreemiumView from "src/modules/purchase/scenes/FreemiumView";
import { withApp } from "src/components/App/Context";

import getSubscriptionProducts from "../../ajax/getSubscriptionProducts";
import StopMonitoringDialog from "./StopMonitoringDialog";
import View from "./View";

const log = window.require("electron-log");
const remote = window.require("@electron/remote");
const { BrowserWindow, getCurrentWindow } = remote;
const mainWindow = getCurrentWindow?.();

class Buy extends Component {
  state = {
    products: null,
    error: false,
    renderStopMonitoringDialog: false,
    checkoutLink: null,
    productId: null,
    isActivatingSubscription: false,
    isPaddleOpen: false,
    isPaddleLoaded: false,
    loadFreemiumTrial: false,
    activeView: this.props.freemiumInitialContent ? "freemium" : "default"
  };
  isUnmounted = false;
  paddleWindow = null;

  async componentDidMount() {
    const { freemiumInitialContent } = this.props;
    if (!freemiumInitialContent && this.shouldMoveToBrowser()) return;
    if (freemiumInitialContent && environment.isBrowser) return;
    try {
      const products = await getSubscriptionProducts();
      if (
        products.yearly.isEligibleForIntroductoryPrice ||
        products.monthly.isEligibleForIntroductoryPrice
      ) {
        !this.isUnmounted && this.setState({ loadFreemiumTrial: true });
      }
      debug.purchase("Received products", products);
      !this.isUnmounted && this.setState({ products });
    } catch (error) {
      log.error(error);
      this.setState({ error: true });
    }
  }

  componentWillUnmount = () => {
    this.isUnmounted = true;
    electronOnly(() => {
      mainWindow.off("move", this.setPaddleWindowBounds);
      mainWindow.off("resize", this.setPaddleWindowBounds);
    });
  };

  getPaddleWindowBounds = () => {
    const mainWindowBounds = mainWindow.getContentBounds();
    const minimalDimensions = 480;

    const width = Math.trunc(
      Math.max(minimalDimensions, mainWindowBounds.width * 0.7)
    );
    const height = Math.trunc(
      Math.max(minimalDimensions, mainWindowBounds.height * 0.85)
    );

    const x = Math.trunc(
      mainWindowBounds.width / 2 + mainWindowBounds.x - width / 2
    );
    const y = Math.trunc(
      mainWindowBounds.height / 2 + mainWindowBounds.y - height / 2
    );
    return { x, y, width, height };
  };

  setPaddleWindowBounds = (e) => {
    this.paddleWindow &&
      this.paddleWindow.setBounds(this.getPaddleWindowBounds());
  };

  setView = (view) => this.setState({ activeView: view });

  closePaddleCheckout = () => {
    mainWindow.off("move", this.setPaddleWindowBounds);
    mainWindow.off("resize", this.setPaddleWindowBounds);
    this.paddleWindow && this.paddleWindow.destroy();
    this.paddleWindow = null;
    this.setState({ isPaddleOpen: false, isPaddleLoaded: false });
  };

  onCheckoutOpen = async ({
    link = this.state.checkoutLink,
    productId = this.state.productId
  }) => {
    const { flow, isAnonymousUser, app, synchronizeCloudAccount } = this.props;

    if (isAnonymousUser) return flow.push("sign-up");
    if (hasPremium(store.getState())) return;
    this.setState({
      isPaddleOpen: true
    });
    const paddleWindow = new BrowserWindow({
      ...this.getPaddleWindowBounds(),
      parent: mainWindow,
      resizable: false,
      movable: false,
      frame: false,
      show: false,
      webPreferences: {
        experimentalFeatures: false,
        nodeIntegration: false,
        webSecurity: true
      }
    });

    this.paddleWindow = paddleWindow;
    paddleWindow.loadURL(link);

    paddleWindow.once("ready-to-show", () => {
      paddleWindow.show();
      this.setState({ isPaddleLoaded: true });
    });

    paddleWindow.webContents.on("will-navigate", (event, url) => {
      if (url.includes("paddle_checkout_success")) {
        mainWindow.off("move", this.setPaddleWindowBounds);
        mainWindow.off("resize", this.setPaddleWindowBounds);
        paddleWindow.destroy();
        this.paddleWindow = null;

        this.setState({
          isActivatingSubscription: true,
          isPaddleOpen: false,
          isPaddleLoaded: false
        });
        setTimeout(async () => {
          try {
            await synchronizeCloudAccount();
            if (!hasPremium(store.getState()) && productId) {
              await createTemporarySubscription(productId);
              await synchronizeCloudAccount();
            }
            app.synchronizer.announceItemChange(
              app.synchronizer.SynchronizableItems.CLOUD_ACCOUNT
            );
            flow.push("purchase-success");
          } catch (error) {
            log.error(error);
            remote.app.relaunch();
            remote.app.quit();
          }
        }, 4000);
      }
    });

    // on macOS child window follows parent window automatically
    if (environment.platform !== "darwin") {
      mainWindow.on("move", this.setPaddleWindowBounds);
    }
    mainWindow.on("resize", this.setPaddleWindowBounds);
  };

  shouldMoveToBrowser = () => {
    const { onClose } = this.props;
    if (environment.isBrowser) {
      onClose();
      window.open(webLinks.pricing, "_blank");
      return true;
    }
    return false;
  };

  toggleToDefaultView = () => {
    const { setContent } = this.props;
    if (this.shouldMoveToBrowser()) return;
    this.setView("default");
    setContent(null);
  };

  handleStopMonitoringDialogOpen = ({ link, productId }) => {
    this.setState({
      renderStopMonitoringDialog: true,
      checkoutLink: link,
      productId
    });
  };
  closeStopMonitoringDialog = () =>
    this.setState({ renderStopMonitoringDialog: false });
  onStopMonitoring = () => {
    this.setState({ renderStopMonitoringDialog: false }, async () => {
      await this.props.stopMonitoring();
      if (this.shouldMoveToBrowser()) return;
      this.onCheckoutOpen({
        link: this.state.checkoutLink,
        productId: this.state.productId
      });
    });
  };

  render() {
    const {
      products,
      error,
      renderStopMonitoringDialog,
      isActivatingSubscription,
      isPaddleOpen,
      isPaddleLoaded,
      loadFreemiumTrial,
      activeView
    } = this.state;
    const {
      onClose,
      t,
      flow,
      isMonitoring,
      onBack,
      freemiumInitialContent,
      updatePopoverStyle
    } = this.props;

    return (
      <>
        <StopMonitoringDialog
          isOpen={renderStopMonitoringDialog}
          onStop={this.onStopMonitoring}
          onDismiss={this.closeStopMonitoringDialog}
        />
        <Dialog
          isOpen={error}
          heading={t("purchase.buy.failure")}
          type="failure"
          buttons={[
            {
              onClick: onClose,
              text: t("buttons.ok")
            }
          ]}
        />

        {activeView === "default" && (
          <View
            loadFreemiumTrial={loadFreemiumTrial}
            closePaddleCheckout={this.closePaddleCheckout}
            isPaddleLoaded={isPaddleLoaded}
            isPaddleOpen={isPaddleOpen}
            isActivatingSubscription={isActivatingSubscription}
            onClose={onClose}
            products={products}
            onBack={onBack}
            onCheckoutOpen={
              isMonitoring?.()
                ? this.handleStopMonitoringDialogOpen
                : this.onCheckoutOpen
            }
            openRestore={() =>
              flow.push("enter-token", { state: { isRestore: true } })
            }
          />
        )}

        {activeView === "freemium" && (
          <FreemiumView
            onClose={onClose}
            onClick={() => this.toggleToDefaultView()}
            contentSelector={freemiumInitialContent}
            updatePopoverStyle={updatePopoverStyle}
          />
        )}
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    isAnonymousUser: isAnonymousUser(state),
    isFreeVersion: isFreemium(state)
  };
}

const enhance = compose(
  withTranslation(),
  withApp,
  connect(mapStateToProps, { synchronizeCloudAccount })
);

export default enhance(Buy);
